import { useMemo } from 'react';
import { useAuthContext } from 'src/auth/hooks';

export function resolvePermissionRecord(perm) {
  if (!perm) {
    return getDefaultPermissions();
  }

  const permissionFields = [
    'can_create', 'can_view', 'can_update', 'can_delete',
    'can_group', 'can_configure', 'can_collect', 'can_allocate',
    'can_unallocate', 'can_transfer', 'can_result', 'can_approve',
    'can_withdraw', 'can_allpaysee', 'can_export', 'financial_report', 'cash_register_report',
    'gst_report', 'daily_collection_report',
    'student_report', 'balance_report', 'hostel_mess_balance_report', 'report_card',
    'student_test_analysis', 'student_question_wise_analysis',
    'admission_report', 'admission_summary', 'batch_report',
    'batch_topper', 'examination_report', 'test_result',
    'exam_question_wise_analysis', 'can_calculate', 'can_recompile',
    'can_add_material',
  ];

  const result = {};
  permissionFields.forEach((field) => {
    const camelCaseField = field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelCaseField] = perm[field] === true;
  });

  const legacyViewFlags = [perm.tasks_view, perm.tasks, perm.can_view, perm.view];
  result.canView = result.canView || legacyViewFlags.some((value) => value === true);

  return result;
}

/**
 * Hook to retrieve permission flags for a given non‑academic module.
 * Returns an object with all permission booleans from the permission record.
 */
export default function useModulePermission(moduleName) {
  const { user } = useAuthContext();

  return useMemo(() => {
    // If user is admin, grant all permissions
    if (user?.is_admin === true) {
      return {
        canCreate: true,
        canUpdate: true,
        canDelete: true,
        canView: true,
        canGroup: true,
        canConfigure: true,
        canCollect: true,
        canAllocate: true,
        canUnallocate: true,
        canTransfer: true,
        canResult: true,
        canApprove: true,
        canWithdraw: true,
        canAllpaysee: true,
        canExport: true,
        canCalculate: true,
        canRecompile: true,
        financialReport: true,
        cashRegisterReport: true,
        gstReport: true,
        dailyCollectionReport: true,
        studentReport: true,
        balanceReport: true,
        hostelMessBalanceReport: true,
        reportCard: true,
        studentTestAnalysis: true,
        studentQuestionWiseAnalysis: true,
        admissionReport: true,
        admissionSummary: true,
        batchReport: true,
        batchTopper: true,
        examinationReport: true,
        testResult: true,
        examQuestionWiseAnalysis: true,
        canAddMaterial: true,
      };
    }

    const perms = user?.non_academic_role_permissions;
    if (!Array.isArray(perms)) {
      return getDefaultPermissions();
    }

    const perm = perms.find((p) => p.module && p.module.toLowerCase() === String(moduleName).toLowerCase());
    if (!perm) {
      return getDefaultPermissions();
    }

    return resolvePermissionRecord(perm);
  }, [user, moduleName]);
}

// Helper function to return default permissions (all false)
function getDefaultPermissions() {
  return {
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    canView: false,
    canGroup: false,
    canConfigure: false,
    canCollect: false,
    canAllocate: false,
    canUnallocate: false,
    canTransfer: false,
    canResult: false,
    canApprove: false,
    canWithdraw: false,
    canAllpaysee: false,
    canExport: false,
    canCalculate: false,
    canRecompile: false,
    financialReport: false,
    cashRegisterReport: false,
    gstReport: false,
    dailyCollectionReport: false,
    studentReport: false,
    balanceReport: false,
    hostelMessBalanceReport: false,
    reportCard: false,
    studentTestAnalysis: false,
    studentQuestionWiseAnalysis: false,
    admissionReport: false,
    admissionSummary: false,
    batchReport: false,
    batchTopper: false,
    examinationReport: false,
    testResult: false,
    examQuestionWiseAnalysis: false,
    canAddMaterial: false,
  };
}
